import apiClient from "./api-client";
import { handleApiError } from "./error-handler";
import { 
  Project, 
  ProjectCreateRequest, 
  ProjectUpdateRequest,
  ProjectStatusUpdateRequest
} from "../models/Project";
import { ProjectBrief } from "../models/ProjectBrief";
import { PaginatedResponse } from "../models/Pagination";
import { User } from "../models/User";
import { ProjectUser } from "../models/ProjectUser";
import { ProjectFreelancerProfile } from "../models/ProjectFreelancerProfile";
import { ProjectDocument } from "../models/ProjectDocument";
import { FreelancerProposal } from "../models/FreelancerProposal";
import { LanguageCode } from "../models/enums/LanguageCode";
import { SignatoryClient, SignatoryClientRequest } from "../models/SignatoryClient";

/**
 * Get paginated list of projects
 * @param page Page number (1-based)
 * @param pageSize Items per page
 * @param status Optional status filter
 * @param name Optional name filter
 * @param projectNumber Optional project number filter
 * @param companyId Optional company ID filter
 * @param sortField Optional sort field
 * @param sortDirection Optional sort direction ('asc' or 'desc')
 */
export const getProjects = async (
  page = 1, 
  pageSize = 10,
  status?: string,
  name?: string,
  projectNumber?: string,
  companyId?: number,
  sortField?: string,
  sortDirection?: string
): Promise<PaginatedResponse<Project>> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    
    if (status) params.append('status', status);
    if (name) params.append('name', name);
    if (projectNumber) params.append('projectNumber', projectNumber);
    if (companyId) params.append('companyId', companyId.toString());
    if (sortField) params.append('sortField', sortField);
    if (sortDirection) params.append('sortDirection', sortDirection);
    
    const response = await apiClient.get<PaginatedResponse<Project>>(`/projects?${params.toString()}`);
    
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch projects");
  }
};

/**
 * Get my projects (for company users) - uses the same endpoint as getProjects
 * The backend automatically filters projects based on user role and permissions
 * @param page Page number (1-based)
 * @param pageSize Items per page
 * @param status Optional status filter
 * @param name Optional name filter
 * @param projectNumber Optional project number filter
 * @param companyId Optional company ID filter
 * @param sortField Optional sort field
 * @param sortDirection Optional sort direction ('asc' or 'desc')
 */
export const getMyProjects = async (
  page = 1, 
  pageSize = 10,
  status?: string,
  name?: string,
  projectNumber?: string,
  companyId?: number,
  sortField?: string,
  sortDirection?: string
): Promise<PaginatedResponse<Project>> => {
  // Use the same endpoint as getProjects - the backend handles the filtering
  return getProjects(page, pageSize, status, name, projectNumber, companyId, sortField, sortDirection);
};

/**
 * Get a specific project by ID
 * @param id Project ID
 */
export const getProject = async (id: number): Promise<Project> => {
  try {
    const response = await apiClient.get(`/projects/${id}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch project");
  }
};

/**
 * Create a new project
 * @param project Project data
 */
export const createProject = async (project: ProjectCreateRequest): Promise<Project> => {
  try {
    const response = await apiClient.post('/projects', project);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to create project");
  }
};

/**
 * Update a project
 * @param id Project ID
 * @param project Updated project data
 */
export const updateProject = async (
  id: number, 
  project: ProjectUpdateRequest
): Promise<Project> => {
  try {
    const response = await apiClient.put(`/projects/${id}`, project);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to update project");
  }
};

/**
 * Patch a project using JSON Patch operations
 * @param id Project ID
 * @param patchOperations JSON Patch operations
 */
export const patchProject = async (
  id: number,
  patchOperations: Array<{
    op: 'add' | 'remove' | 'replace';
    path: string;
    value?: any;
  }>
): Promise<void> => {
  try {
    await apiClient.patch(`/projects/${id}`, patchOperations);
  } catch (error) {
    throw handleApiError(error, "Failed to patch project");
  }
};

/**
 * Update project status
 * @param id Project ID
 * @param statusUpdate Status update data
 */
export const updateProjectStatus = async (
  id: number,
  statusUpdate: ProjectStatusUpdateRequest
): Promise<void> => {
  try {
    await apiClient.patch(`/projects/${id}/status`, statusUpdate);
  } catch (error) {
    throw handleApiError(error, "Failed to update project status");
  }
};

/**
 * Submit a project (change status from Draft to Submitted)
 * @param id Project ID
 */
export const submitProject = async (id: number): Promise<void> => {
  try {
    await apiClient.patch(`/projects/${id}/status`, {
      status: "Submitted"
    });
  } catch (error) {
    throw handleApiError(error, "Failed to submit project");
  }
};


/**
 * Delete a project
 * @param id Project ID
 */
export const deleteProject = async (id: number): Promise<void> => {
  try {
    await apiClient.delete(`/projects/${id}`);
  } catch (error) {
    throw handleApiError(error, "Failed to delete project");
  }
};

/**
 * Get the project brief for a specific project
 * @param id Project ID
 */
export const getProjectBrief = async (id: number): Promise<ProjectBrief> => {
  try {
    const response = await apiClient.get(`/projects/${id}/brief`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch project brief");
  }
};

export interface ProjectArchiveInfo {
  archivedAt: string;
  archivedByFirstName: string;
  archivedByLastName: string;
}

/**
 * Get archive information for a project (who archived it and when)
 * @param id Project ID
 */
export const getProjectArchiveInfo = async (id: number): Promise<ProjectArchiveInfo> => {
  try {
    const response = await apiClient.get(`/projects/${id}/archive-info`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch project archive info");
  }
};

/**
 * Add a user to a project
 * @param projectId Project ID
 * @param userId User ID to add
 */
export const addUserToProject = async (projectId: number, userId: number): Promise<void> => {
  try {
    await apiClient.post(`/projects/${projectId}/users`, { userId });
  } catch (error) {
    throw handleApiError(error, "Failed to add user to project");
  }
};

/**
 * Remove a user from a project
 * @param projectId Project ID
 * @param userId User ID to remove
 */
export const removeUserFromProject = async (projectId: number, userId: number): Promise<void> => {
  try {
    await apiClient.delete(`/projects/${projectId}/users/${userId}`);
  } catch (error) {
    throw handleApiError(error, "Failed to remove user from project");
  }
};

/**
 * Get available users for a project (users that can be added)
 * @param projectId Project ID
 */
export const getAvailableUsersForProject = async (projectId: number): Promise<User[]> => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/available-users`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch available users for project");
  }
};

/**
 * Get available client users for a project with enriched client information
 * @param projectId Project ID
 */
export const getAvailableClientUsersForProject = async (projectId: number): Promise<ProjectUser[]> => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/available-client-users`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch available client users for project");
  }
};

/**
 * Get available internal users for a project with enriched internal information
 * @param projectId Project ID
 */
export const getAvailableInternalUsersForProject = async (projectId: number): Promise<ProjectUser[]> => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/available-internal-users`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch available internal users for project");
  }
};

/**
 * Get all available users for a project with enriched information based on user roles
 * @param projectId Project ID
 * @param userRoles Current user roles
 */
export const getAvailableEnrichedUsersForProject = async (
  projectId: number, 
  userRoles: string[] = []
): Promise<ProjectUser[]> => {
  try {
    const isCSM = userRoles.some(role => role.toLowerCase() === 'csm');
    
    if (isCSM) {
      // CSM can see both client and internal users
      const [clientUsers, internalUsers] = await Promise.all([
        getAvailableClientUsersForProject(projectId),
        getAvailableInternalUsersForProject(projectId)
      ]);
      return [...clientUsers, ...internalUsers];
    } else {
      // Clients can only see available client users
      return await getAvailableClientUsersForProject(projectId);
    }
  } catch (error) {
    throw handleApiError(error, "Failed to fetch available enriched users for project");
  }
};

/**
 * Get associated client users for a project with enriched information (client users already added to the project)
 * @param projectId Project ID
 */
export const getAssociatedClientUsersForProject = async (projectId: number): Promise<ProjectUser[]> => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/associated-client-users`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch associated client users for project");
  }
};

/**
 * Get associated internal users for a project (internal users already added to the project)
 * @param projectId Project ID
 */
export const getAssociatedInternalUsersForProject = async (projectId: number): Promise<ProjectUser[]> => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/associated-internal-users`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch associated internal users for project");
  }
};

/**
 * Get all associated users for a project with enriched information (combines client and internal already added to the project)
 * @param projectId Project ID
 */
export const getAssociatedUsersForProject = async (projectId: number): Promise<ProjectUser[]> => {
  try {
    const [clientUsers, internalUsers] = await Promise.all([
      getAssociatedClientUsersForProject(projectId),
      getAssociatedInternalUsersForProject(projectId)
    ]);
    return [...clientUsers, ...internalUsers];
  } catch (error) {
    throw handleApiError(error, "Failed to fetch associated users for project");
  }
};

/**
 * Project invitation request interface
 */
export interface ProjectInvitationRequest {
  email: string;
  firstName: string;
  lastName: string;
  role: number;  // UserRole enum value (CLIENT = 4)
  language?: LanguageCode;  // Language for the invitation email
}

/**
 * Invite user to project response
 */
export interface InviteUserResponse {
  success: boolean;
  onboardingInProgress: boolean;
}

/**
 * Invite a user to a project
 * @param projectId Project ID
 * @param invitation Invitation data
 */
export const inviteUserToProject = async (
  projectId: number,
  invitation: ProjectInvitationRequest
): Promise<InviteUserResponse> => {
  try {
    const response = await apiClient.post<InviteUserResponse>(`/projects/${projectId}/invitations`, invitation);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to invite user to project");
  }
};

/**
 * Get freelancer profiles associated with a project
 * @param projectId Project ID
 */
export const getProjectFreelancerProfiles = async (projectId: number): Promise<ProjectFreelancerProfile[]> => {
  try {
    const response = await apiClient.get(`/project-freelancer-profiles/projects/${projectId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch project freelancer profiles");
  }
};

/**
 * Get freelancer's associated projects (uses same endpoint as regular projects)
 * @param page Page number (1-based)
 * @param pageSize Items per page
 * @param status Optional status filter
 * @param name Optional name filter
 * @param projectNumber Optional project number filter
 * @param sortField Optional sort field
 * @param sortDirection Optional sort direction ('asc' or 'desc')
 */
export const getFreelancerProjects = async (
  page = 1,
  pageSize = 10,
  status?: string,
  name?: string,
  projectNumber?: string,
  sortField?: string,
  sortDirection?: string
): Promise<PaginatedResponse<Project>> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (status) params.append('status', status);
    if (name) params.append('name', name);
    if (projectNumber) params.append('projectNumber', projectNumber);
    if (sortField) params.append('sortField', sortField);
    if (sortDirection) params.append('sortDirection', sortDirection);

    const response = await apiClient.get<PaginatedResponse<Project>>(`/projects?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch freelancer projects");
  }
};

/**
 * Upload a document to a project
 * @param projectId Project ID
 * @param document Document data
 */
export const uploadProjectDocument = async (
  projectId: number, 
  document: { name: string; content: string; type?: any }
): Promise<ProjectDocument> => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/documents`, document);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to upload document");
  }
};

/**
 * Get all documents for a project
 * @param projectId Project ID
 */
export const getProjectDocuments = async (
  projectId: number
): Promise<ProjectDocument[]> => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/documents`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch project documents");
  }
};

/**
 * Delete a project document
 * @param projectId Project ID
 * @param documentId Document ID
 */
export const deleteProjectDocument = async (
  projectId: number,
  documentId: number
): Promise<void> => {
  try {
    await apiClient.delete(`/projects/${projectId}/documents/${documentId}`);
  } catch (error) {
    throw handleApiError(error, "Failed to delete document");
  }
};

/**
 * Get freelancer proposal for a specific project and freelancer
 * @param projectId Project ID
 * @param freelancerProfileId Freelancer profile ID
 */
export const getFreelancerProposal = async (
  projectId: number,
  freelancerProfileId: number
): Promise<FreelancerProposal | null> => {
  try {
    const response = await apiClient.get(`/freelancer-proposals`, {
      params: { projectId, freelancerProfileId }
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404 || error.response?.status === 204) {
      // No proposal exists yet (NotFound or NoContent)
      return null;
    }
    throw handleApiError(error, "Failed to get freelancer proposal");
  }
};

/**
 * Get all available project statuses
 * @returns Array of project status strings
 */
export const getProjectStatuses = async (): Promise<string[]> => {
  try {
    const response = await apiClient.get<string[]>('/projects/statuses');
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch project statuses");
  }
};

// ================================
// Signatory Client API
// ================================

/**
 * Get the signatory client for a project
 * @param projectId Project ID
 */
export const getProjectSignatoryClient = async (projectId: number): Promise<SignatoryClient | null> => {
  try {
    const response = await apiClient.get<SignatoryClient>(`/projects/${projectId}/signatory-client`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw handleApiError(error, "Failed to fetch signatory client");
  }
};

/**
 * Set or update the signatory client for a project
 * @param projectId Project ID
 * @param signatoryClient Signatory client data
 */
export const setProjectSignatoryClient = async (
  projectId: number,
  signatoryClient: SignatoryClientRequest
): Promise<SignatoryClient> => {
  try {
    const response = await apiClient.post<SignatoryClient>(
      `/projects/${projectId}/signatory-client`,
      signatoryClient
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to set signatory client");
  }
};

/**
 * Remove the signatory client from a project
 * @param projectId Project ID
 */
export const removeProjectSignatoryClient = async (projectId: number): Promise<void> => {
  try {
    await apiClient.delete(`/projects/${projectId}/signatory-client`);
  } catch (error) {
    throw handleApiError(error, "Failed to remove signatory client");
  }
};

/**
 * Search for an existing signatory client by email
 * @param email Email to search
 * @returns SignatoryClient if found, null if not found
 */
export const searchSignatoryClientByEmail = async (email: string): Promise<SignatoryClient | null> => {
  try {
    const response = await apiClient.get<SignatoryClient>(`/projects/signatory-clients/search`, {
      params: { email }
    });
    return response.data;
  } catch (error: unknown) {
    if ((error as { response?: { status?: number } })?.response?.status === 404) {
      return null;
    }
    throw handleApiError(error, "Failed to search signatory client");
  }
};

